// frontend/src/services/gemini.service.ts
//
// Bu servis Gemini'ye DOĞRUDAN bağlanmaz — kendi backend'imizdeki
// /api/ai/* proxy endpoint'lerini çağırır. API anahtarı yalnızca backend'de
// (process.env.GEMINI_API_KEY) tutulur, frontend'e hiçbir zaman gönderilmez.

import {
  ChatTurn,
  GeminiModel,
  StudentContext,
  MentorChatResponse,
  CodeEvaluationResult,
  DetailedCodeEvaluationResult,
  QuizQuestion,
  ApiError,
} from '../types/ai.types';
import { authService } from './auth.service';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

class GeminiApiError extends Error {
  code?: string;
  status: number;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'GeminiApiError';
    this.status = status;
    this.code = code;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = await authService.getAccessToken();
  if (!token) {
    throw new GeminiApiError('Oturum bulunamadı, lütfen giriş yapın.', 401, 'NO_SESSION');
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = data as { error?: ApiError };
    throw new GeminiApiError(
      err?.error?.message || `İstek başarısız (HTTP ${res.status})`,
      res.status,
      err?.error?.code
    );
  }

  return data as T;
}

export const geminiService = {
  /**
   * AI Mentor'a mesaj gönderir, sohbet geçmişini dahil eder.
   */
  async sendChatMessage(
    message: string,
    history: ChatTurn[],
    options?: { model?: GeminiModel; studentContext?: StudentContext; isLessonRequest?: boolean }
  ): Promise<MentorChatResponse> {
    return postJson<MentorChatResponse>('/api/ai/mentor/chat', {
      message,
      history,
      model: options?.model,
      studentContext: options?.studentContext,
      isLessonRequest: options?.isLessonRequest,
    });
  },

  /**
   * Öğrenci kodunu değerlendirir (kalite, mantık, temizlik, performans).
   */
  async evaluateCode(
    code: string,
    language = 'python',
    model?: GeminiModel
  ): Promise<CodeEvaluationResult> {
    return postJson<CodeEvaluationResult>('/api/ai/code/evaluate', { code, language, model });
  },

  /**
   * Code Editor sayfası için: kalite/mantık/okunabilirlik 3 ayrı puan.
   * Çalıştırılmış kodun çıktısı varsa, daha isabetli bir değerlendirme için dahil edilir.
   */
  async evaluateCodeDetailed(
    code: string,
    runtimeOutput: string | null,
    language = 'python',
    model?: GeminiModel
  ): Promise<DetailedCodeEvaluationResult> {
    return postJson<DetailedCodeEvaluationResult>('/api/ai/code/evaluate-detailed', {
      code,
      runtimeOutput,
      language,
      model,
    });
  },

  /**
   * Konuya özel quiz soruları üretir.
   */
  async generateQuiz(params: {
    topic: string;
    difficulty: 'easy' | 'medium' | 'hard';
    questionCount?: number;
    types?: QuizQuestion['type'][];
  }): Promise<QuizQuestion[]> {
    const data = await postJson<{ questions: QuizQuestion[] }>('/api/ai/quiz/generate', params);
    return data.questions;
  },

  /**
   * Metni sese dönüştürür (ElevenLabs) ve çalınabilir bir Audio URL'si döndürür.
   */
  async generateSpeech(text: string): Promise<string> {
    const token = await authService.getAccessToken();
    if (!token) {
      throw new GeminiApiError('Oturum bulunamadı.', 401, 'NO_SESSION');
    }

    const res = await fetch(`${API_BASE}/api/ai/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new GeminiApiError(
        data?.error?.message || `Ses üretimi başarısız (HTTP ${res.status})`,
        res.status
      );
    }

    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

export { GeminiApiError };
